import { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import ThumbUpAltRoundedIcon from '@mui/icons-material/ThumbUpAltRounded'
import ThumbUpOffAltRoundedIcon from '@mui/icons-material/ThumbUpOffAltRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import {
  engageService,
  type Poll,
  type Question,
} from '../services/engageService'
import { useAttendeeAuth } from '../contexts/AttendeeAuthContext'

/**
 * Live polls + Q&A for a session. Renders only for signed-in attendees. Listens
 * to the app's SSE 'vc:changed' events (kind 'poll'/'qa') to refresh live.
 */
export default function SessionLive({ sessionId }: { sessionId: string }) {
  const { isAuthenticated } = useAttendeeAuth()
  const [polls, setPolls] = useState<Poll[]>([])
  const [questions, setQuestions] = useState<Question[]>([])
  const [draft, setDraft] = useState('')
  const [anonymous, setAnonymous] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const refresh = useCallback(() => {
    if (!isAuthenticated) return
    Promise.all([
      engageService.listPolls(sessionId).catch(() => []),
      engageService.listQuestions(sessionId).catch(() => []),
    ]).then(([p, q]) => {
      setPolls(p)
      setQuestions(q)
      setLoaded(true)
    })
  }, [isAuthenticated, sessionId])

  useEffect(() => {
    refresh()
    const onChanged = (e: Event) => {
      const kind = (e as CustomEvent).detail?.kind
      if (kind === 'poll' || kind === 'qa') refresh()
    }
    window.addEventListener('vc:changed', onChanged)
    return () => window.removeEventListener('vc:changed', onChanged)
  }, [refresh])

  if (!isAuthenticated || !loaded) return null
  if (polls.length === 0 && questions.length === 0 && !draft) {
    // Still show the Q&A composer so attendees can start asking.
  }

  const vote = async (pollId: string, optionId: string) => {
    try {
      const updated = await engageService.vote(pollId, optionId)
      setPolls((prev) => prev.map((p) => (p.id === pollId ? updated : p)))
    } catch {
      refresh()
    }
  }

  const upvote = async (qid: string) => {
    // optimistic
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qid
          ? { ...q, upvoted: !q.upvoted, upvotes: q.upvotes + (q.upvoted ? -1 : 1) }
          : q,
      ),
    )
    try {
      await engageService.toggleUpvote(qid)
    } catch {
      refresh()
    }
  }

  const submit = async () => {
    const body = draft.trim()
    if (!body) return
    setDraft('')
    try {
      await engageService.submitQuestion(body, sessionId, anonymous)
      refresh()
    } catch {
      refresh()
    }
  }

  return (
    <Box>
      <Divider sx={{ my: 3 }} />
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Live
      </Typography>

      {polls.map((poll) => (
        <Card key={poll.id} variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
              {poll.question}
            </Typography>
            {poll.status === 'closed' && <Chip size="small" label="Closed" />}
          </Stack>
          <Stack spacing={1}>
            {poll.options.map((o) => {
              const pct = poll.totalVotes ? Math.round((o.votes / poll.totalVotes) * 100) : 0
              const mine = poll.myOptionId === o.id
              const votable = poll.status === 'open'
              return (
                <Box
                  key={o.id}
                  onClick={() => votable && vote(poll.id, o.id)}
                  sx={{
                    cursor: votable ? 'pointer' : 'default',
                    border: '1px solid',
                    borderColor: mine ? 'primary.main' : 'divider',
                    borderRadius: 2,
                    p: 1,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography variant="body2" fontWeight={mine ? 700 : 500}>
                      {o.label}
                      {mine ? ' ✓' : ''}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {pct}% ({o.votes})
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              )
            })}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {poll.totalVotes} {poll.totalVotes === 1 ? 'vote' : 'votes'}
            {poll.status === 'open' ? ' · tap an option to vote' : ''}
          </Typography>
        </Card>
      ))}

      {/* Q&A */}
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 1, mb: 1 }}>
        Q&amp;A
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Ask a question…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
          }}
        />
        <Button variant="contained" onClick={submit} disabled={!draft.trim()}>
          Ask
        </Button>
      </Stack>
      <FormControlLabel
        sx={{ mb: 2 }}
        control={
          <Checkbox
            size="small"
            checked={anonymous}
            onChange={(e) => setAnonymous(e.target.checked)}
          />
        }
        label={
          <Typography variant="caption" color="text.secondary">
            Ask anonymously (your name won't be shown to other attendees)
          </Typography>
        }
      />
      <Stack spacing={1}>
        {questions.map((q) => (
          <Card key={q.id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Stack alignItems="center" sx={{ minWidth: 44 }}>
                <Button
                  size="small"
                  onClick={() => upvote(q.id)}
                  sx={{ minWidth: 0, p: 0.5, flexDirection: 'column' }}
                  color={q.upvoted ? 'primary' : 'inherit'}
                >
                  {q.upvoted ? (
                    <ThumbUpAltRoundedIcon fontSize="small" />
                  ) : (
                    <ThumbUpOffAltRoundedIcon fontSize="small" />
                  )}
                  <Typography variant="caption" fontWeight={700}>
                    {q.upvotes}
                  </Typography>
                </Button>
              </Stack>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2">{q.body}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {q.authorName || 'Anonymous'}
                  </Typography>
                  {q.answered && (
                    <Chip
                      size="small"
                      icon={<CheckCircleRoundedIcon sx={{ fontSize: 14 }} />}
                      label="Answered"
                      color="success"
                      sx={{ height: 20 }}
                    />
                  )}
                </Stack>
                {q.answer && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      borderRadius: 1.5,
                      bgcolor: 'success.50',
                      borderLeft: '3px solid',
                      borderColor: 'success.main',
                    }}
                  >
                    <Typography variant="body2">{q.answer}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Answered{q.answeredBy ? ` by ${q.answeredBy}` : ''}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Box>
  )
}
